import { APP_SOURCES, FormBlock, FormData } from "vmd-types";

import { isArcherCustomId } from "../../utils/is-archer-custom-id";

const ADOIT_ID_FIELD_NAME = "ADOIT ID";

const isBlank = (value: FormData[string] | undefined): boolean => {
  if (value === undefined || value === null) return true;
  if (Array.isArray(value))
    return value.every((entry) => !String(entry).trim());
  return !String(value).trim();
};

/**
 * A record that only exists in one of the two systems has every column of the
 * other one empty, yet the blueprint's per-question source is static. Entity-ref
 * questions still render a value, because their master data column was filled
 * from whichever system delivered the record, so their source label is remapped
 * per item. Questions outside master data stay empty and keep their configured
 * label.
 *
 * Both ids are checked, the same pair the Record Source column reads, so the
 * badge never contradicts it:
 * (i) an Archer id and no ADOIT id remaps ADOIT to Archer;
 * (ii) an ADOIT id and no Archer id remaps Archer to ADOIT;
 * (iii) both ids or neither leaves every label as configured, because an
 * imported row can arrive with a name and neither id, and its fields are no
 * more Archer's than ADOIT's.
 *
 * Only a BAID-prefixed customId counts as an Archer id. The ADOIT-only import
 * writes the bare ADOIT id into that column, since the Excel upload rejects
 * rows without an id.
 *
 * Returns undefined when the blueprint has no ADOIT id question.
 */
export function buildEntityRefSourceOverrides(
  blocks: FormBlock[] | undefined,
  formData: FormData | undefined,
  customId: string | undefined
): Record<string, string> | undefined {
  const adoitIdQuestion = (blocks ?? [])
    .flatMap((block) => block.questions ?? [])
    .find(
      (question) =>
        question.fieldName?.trim() === ADOIT_ID_FIELD_NAME ||
        question.label.trim() === ADOIT_ID_FIELD_NAME
    );
  if (!adoitIdQuestion) return undefined;

  const hasAdoitId = !isBlank(formData?.[adoitIdQuestion.id]);
  const hasArcherId = isArcherCustomId(customId);

  if (hasArcherId && !hasAdoitId) {
    return { [APP_SOURCES.ADOIT]: APP_SOURCES.ARCHER };
  }
  if (hasAdoitId && !hasArcherId) {
    return { [APP_SOURCES.ARCHER]: APP_SOURCES.ADOIT };
  }
  return undefined;
}
